'use client';

import { cn } from '@/libs';

import { getPaginationData } from './utils';

import { Button } from '../Button';
import { Icon } from '../Icon';

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxVisible?: number;
  loading?: boolean;
  showLoadingSpinner?: boolean;
  className?: string;
  'data-cy'?: string;
};

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  maxVisible,
  loading,
  showLoadingSpinner,
  className,
  ...props
}: PaginationProps) => {
  const pagination = getPaginationData({
    currentPage,
    totalPages,
    onPageChange,
    maxVisible,
    loading,
  });

  if (totalPages <= 1) return null;

  return (
    <div
      data-cy={props['data-cy']}
      className={cn('flex items-center justify-center space-x-2', className)}
    >
      <Button
        variant="ghost"
        size="small"
        aria-label="Página anterior"
        onClick={pagination.goToPrevious}
        disabled={!pagination.canGoPrevious || pagination.loading}
        className={cn(
          'p-2 rounded',
          !pagination.canGoPrevious || pagination.loading
            ? 'text-foreground/30 cursor-not-allowed'
            : 'text-foreground/80 hover:text-foreground/60 hover:bg-foreground/10',
        )}
      >
        <Icon.ChevronLeft className="h-4 w-4" />
      </Button>

      {pagination.pages.map((page) => (
        <Button
          key={page}
          variant="ghost"
          size="small"
          aria-label={`Página ${page}`}
          aria-current={pagination.currentPage === page ? 'page' : undefined}
          onClick={() => pagination.onPageChange(page)}
          disabled={pagination.loading}
          className={cn(
            'px-3 py-2 rounded text-sm font-medium',
            pagination.loading
              ? 'text-foreground/30 cursor-not-allowed'
              : pagination.currentPage === page
                ? 'bg-foreground/20 text-foreground'
                : 'text-foreground/80 hover:text-foreground/60 hover:bg-foreground/10',
          )}
        >
          {page}
        </Button>
      ))}

      <Button
        variant="ghost"
        size="small"
        aria-label="Próxima página"
        onClick={pagination.goToNext}
        disabled={!pagination.canGoNext || pagination.loading}
        className={cn(
          'p-2 rounded',
          !pagination.canGoNext || pagination.loading
            ? 'text-foreground/30 cursor-not-allowed'
            : 'text-foreground/80 hover:text-foreground/60 hover:bg-foreground/10',
        )}
      >
        <Icon.ChevronRight className="h-4 w-4" />
      </Button>

      {showLoadingSpinner && pagination.loading && (
        <Button variant="ghost" size="small" loading loadingText="" aria-hidden="true" />
      )}
    </div>
  );
};
